import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShieldCheck, Heart, Users, Crown, ArrowRight, Lock, Award } from "lucide-react";

const HERO_IMG = "/hero-sanctuary.jpg";
const MODEL_1 = "https://images.unsplash.com/photo-1767801291712-3dc2acec02a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const MODEL_2 = "https://images.pexels.com/photos/19083434/pexels-photo-19083434.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";
const COUPLE = "https://images.pexels.com/photos/36833321/pexels-photo-36833321.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";

export default function Landing() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("dating");
  const [founder, setFounder] = useState({ founder_count: 0, spots_left: 100, is_open: true });

  useEffect(() => {
    api.get("/founder-status").then(({ data }) => setFounder(data)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen bg-[#1A1A1A] text-[#F5F5F5] overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-12 pb-32" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.55]"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/75 to-[#1A1A1A]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1A1A1A]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-12 sm:pt-20">
          {founder.is_open && (
            <div className="inline-flex items-center gap-3 px-5 py-2.5 goji-glass mb-10" data-testid="founder-badge">
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-label text-[0.65rem] text-[#D4AF37]">Founders Circle</span>
              <span className="text-sm text-[#F5F5F5]/80">
                <span className="text-[#D4AF37]">{founder.spots_left}</span> / 100 places remain
              </span>
              <span className="text-xs text-[#F5F5F5]/55 font-label tracking-[0.18em]">— FREE FOR LIFETIME — FOUNDER STATUS</span>
            </div>
          )}

          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl xl:text-[8.5rem] font-normal tracking-tight leading-[0.95] max-w-5xl" data-testid="hero-headline">
            A sovereign space<br />
            <span className="italic text-[#D4AF37]">to be chosen.</span>
          </h1>

          <p className="mt-10 text-lg sm:text-xl max-w-2xl text-[#F5F5F5]/65 leading-relaxed" data-testid="hero-subline">
            Goji is a private sanctuary for considered connection — for romance, or for chosen family.
            Every member verified. Every encounter, intentional.
          </p>

          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <button onClick={() => navigate("/auth?mode=signup")} className="btn-ghost flex items-center gap-2" data-testid="hero-cta-join">
              Claim Your Place <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <Link to="/pricing" className="btn-ghost" data-testid="hero-cta-pricing">
              View Memberships
            </Link>
          </div>

          {/* Mode Toggle */}
          <div className="mt-24 max-w-4xl">
            <span className="font-label text-[0.65rem] text-[#D4AF37]">Two paths. One sanctuary.</span>

            <div className="mt-5 goji-glass flex" data-testid="mode-toggle">
              <button
                onClick={() => setMode("dating")}
                className={`flex-1 py-5 px-6 font-label text-[0.72rem] transition-colors ${
                  mode === "dating" ? "bg-[#D4AF37]/10 text-[#D4AF37] border-r border-[#D4AF37]/30" : "text-[#F5F5F5]/45 hover:text-[#F5F5F5]/80 border-r border-[#D4AF37]/15"
                }`}
                data-testid="mode-toggle-dating"
              >
                <Heart className="inline w-3.5 h-3.5 mr-2 -mt-0.5" /> Romance
              </button>
              <button
                onClick={() => setMode("fam")}
                className={`flex-1 py-5 px-6 font-label text-[0.72rem] transition-colors ${
                  mode === "fam" ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#F5F5F5]/45 hover:text-[#F5F5F5]/80"
                }`}
                data-testid="mode-toggle-fam"
              >
                <Users className="inline w-3.5 h-3.5 mr-2 -mt-0.5" /> Chosen Family
              </button>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              <div className="goji-glass overflow-hidden">
                <img src={mode === "dating" ? COUPLE : MODEL_2} alt="" className="w-full h-64 object-cover opacity-70" />
                <div className="p-7">
                  <h3 className="font-display text-3xl font-normal mb-2 text-[#F5F5F5]">
                    {mode === "dating" ? "For meaningful romance" : "For chosen family"}
                  </h3>
                  <p className="text-[#F5F5F5]/65 text-sm leading-relaxed">
                    {mode === "dating"
                      ? "Curated introductions, considered conversation, and people who show up. Romance for adults who know what they want."
                      : "Real friendship past twenty-five. Compatible values, shared rituals, the people you'd build a life beside."}
                  </p>
                </div>
              </div>

              <div className="goji-glass p-7 flex flex-col justify-between">
                <div>
                  <span className="font-label text-[0.65rem] text-[#D4AF37]">How it works</span>
                  <ul className="mt-5 space-y-4 text-[#F5F5F5]/80 text-sm">
                    <li className="flex gap-4"><span className="text-[#D4AF37] font-label text-xs pt-0.5">01</span> Apply to the circle — a private, considered profile.</li>
                    <li className="flex gap-4"><span className="text-[#D4AF37] font-label text-xs pt-0.5">02</span> Pass verification — identity confirmed, presence reviewed.</li>
                    <li className="flex gap-4"><span className="text-[#D4AF37] font-label text-xs pt-0.5">03</span> Choose your path — romance, chosen family, or both.</li>
                    <li className="flex gap-4"><span className="text-[#D4AF37] font-label text-xs pt-0.5">04</span> Receive introductions curated by quiet intelligence.</li>
                  </ul>
                </div>
                <button onClick={() => navigate("/auth?mode=signup")} className="mt-8 btn-ghost self-start" data-testid="mode-cta-join">
                  Begin
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE VERIFICATION */}
      <section className="relative py-28 px-6 sm:px-10 border-t border-[#D4AF37]/12" data-testid="bouncer-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div className="relative">
            <img src={MODEL_1} alt="" className="w-full object-cover h-[640px] grayscale opacity-80 border border-[#D4AF37]/20" />
            <div className="absolute bottom-6 left-6 right-6 bg-[#1A1A1A]/95 border border-[#D4AF37]/30 p-6 flex items-center gap-4">
              <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
              <div>
                <p className="font-display text-lg leading-tight text-[#F5F5F5]">Every Member Verified</p>
                <p className="text-xs text-[#F5F5F5]/55 mt-0.5">No bots. No imposters. No accidents.</p>
              </div>
            </div>
          </div>

          <div>
            <span className="font-label text-[0.65rem] text-[#D4AF37]">The Verification</span>
            <h2 className="font-display text-5xl sm:text-6xl font-normal mt-4 leading-[1.05] text-[#F5F5F5]">
              A gatekeeper, <span className="italic text-[#D4AF37]">by design.</span>
            </h2>
            <p className="mt-7 text-lg text-[#F5F5F5]/65 leading-relaxed">
              Every applicant is reviewed before joining the circle. A considered intelligence examines presence, intent and authenticity — so the space remains exactly what it is. Private. Honest. Earned.
            </p>
            <div className="mt-12 grid grid-cols-2 gap-px bg-[#D4AF37]/15">
              {[
                { icon: <Lock className="w-4 h-4" />, label: "Profile review", desc: "Identity and presence assessed." },
                { icon: <Award className="w-4 h-4" />, label: "Considered verdict", desc: "Reasoned, never reductive." },
                { icon: <ShieldCheck className="w-4 h-4" />, label: "Private community", desc: "No public profiles, no scraping." },
                { icon: <Crown className="w-4 h-4" />, label: "Verified status", desc: "A quiet mark of presence." },
              ].map((f) => (
                <div key={f.label} className="bg-[#1A1A1A] p-7">
                  <div className="text-[#D4AF37] mb-4">{f.icon}</div>
                  <p className="font-display text-base text-[#F5F5F5]">{f.label}</p>
                  <p className="text-xs text-[#F5F5F5]/55 mt-2 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS */}
      <section className="relative py-28 px-6 sm:px-10 border-t border-[#D4AF37]/12" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-label text-[0.65rem] text-[#D4AF37]">Memberships</span>
            <h2 className="font-display text-5xl sm:text-6xl font-normal mt-4 text-[#F5F5F5]">
              Choose your <span className="italic text-[#D4AF37]">presence.</span>
            </h2>
            {founder.is_open && (
              <p className="mt-6 text-sm text-[#D4AF37] font-label" data-testid="founder-pricing-banner">
                First 100 members — ICON FREE FOR LIFETIME — FOUNDER STATUS
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#D4AF37]/15">
            {[
              { name: "CHIC", price: "14,99", perks: ["10 introductions / day", "Considered AI matching", "Verified status", "Standard correspondence"], cta: "Begin with Chic" },
              { name: "DIVA", price: "29,99", perks: ["Unlimited introductions", "Refined AI matching", "See who has chosen you", "Two profile boosts / week", "Priority verification"], cta: "Step into Diva", popular: true },
              { name: "ICON", price: "49,99", perks: ["Everything in Diva", "ICON quiet mark", "Concierge matchmaker", "Daily profile boost", "Read receipts", "ICON-only gatherings"], cta: "Enter as ICON", icon: true },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`relative bg-[#1A1A1A] p-10 ${tier.icon ? "icon-glow" : ""}`}
                data-testid={`pricing-card-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37] font-label text-[0.6rem] text-[#D4AF37]">
                    Most Chosen
                  </span>
                )}
                {tier.icon && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37] font-label text-[0.6rem] text-[#D4AF37] flex items-center gap-1.5">
                    <Crown className="w-3 h-3" /> Top Tier
                  </span>
                )}

                <h3 className="font-display text-4xl font-normal text-[#D4AF37]">{tier.name}</h3>
                <div className="mt-5 flex items-baseline gap-1 text-[#F5F5F5]">
                  <span className="text-xl text-[#F5F5F5]/60">€</span>
                  <span className="font-display text-6xl font-normal">{tier.price}</span>
                  <span className="text-sm text-[#F5F5F5]/45 ml-1">/ month</span>
                </div>

                <ul className="mt-10 space-y-4">
                  {tier.perks.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-[#F5F5F5]/80">
                      <span className="text-[#D4AF37] mt-1.5 w-2 h-px bg-[#D4AF37] flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate("/auth?mode=signup")}
                  className="mt-10 w-full btn-ghost"
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — quiet static grid */}
      <section className="relative py-28 px-6 sm:px-10 border-t border-[#D4AF37]/12" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-label text-[0.65rem] text-[#D4AF37]">From the circle</span>
            <h2 className="font-display text-4xl sm:text-5xl font-normal mt-4 text-[#F5F5F5]">
              In their <span className="italic text-[#D4AF37]">own words.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#D4AF37]/15">
            {[
              { q: "I met my chosen sister within the week. We share Sunday mornings now.", n: "Camille — Amsterdam" },
              { q: "What I appreciated most was the quiet. No noise, no performance. Just people.", n: "Aria — Berlin" },
              { q: "Goji feels less like an app and more like an introduction at a private dinner.", n: "Nia — Paris" },
              { q: "I came for romance. I left with both a partner and three close friends.", n: "Lex — London" },
              { q: "The verification matters. It's the reason I trust the people I meet here.", n: "Soraya — Lisbon" },
              { q: "Considered, intentional, and deeply private. It's how connection should feel.", n: "Yara — Copenhagen" },
            ].map((t) => (
              <div key={t.n} className="bg-[#1A1A1A] p-10">
                <p className="font-display text-xl italic leading-snug text-[#F5F5F5]/85">"{t.q}"</p>
                <p className="mt-6 font-label text-[0.65rem] text-[#D4AF37]">{t.n}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 px-6 border-t border-[#D4AF37]/12" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-5xl sm:text-6xl font-normal leading-[1.05] text-[#F5F5F5]">
            The circle is forming.<br />
            <span className="italic text-[#D4AF37]">Will you be present?</span>
          </h2>
          <p className="mt-7 text-[#F5F5F5]/65 text-lg max-w-xl mx-auto">
            Apply to the founders circle. Founder status is granted only to the first one hundred — and is held for life.
          </p>
          <button onClick={() => navigate("/auth?mode=signup")} className="mt-12 btn-ghost" data-testid="final-cta-button">
            Apply to Goji
          </button>
          {founder.is_open && (
            <p className="mt-6 font-label text-[0.65rem] text-[#D4AF37]">
              {founder.spots_left} of 100 places remain
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
